import Button from 'tio-ui/components/button';
import { or } from 'tio-ui/utilities';
import { HStack, Section, VStack } from 'tio-ui/components/layout';
import Modal from 'tio-ui/components/modal';
import Footer from 'tio-ui/components/footer';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedFunction } from 'reactiveweb/function';
import Component from '@glimmer/component';
import ENV from 'tio-employee/config/environment';
import {
  settleTasPaymentRequest,
  cancelTasPaymentRequest,
  downloadTasPaymentFiles,
} from 'tio-data/requests/tas-payment';
import pageTitle from 'ember-page-title/helpers/page-title';
import Paginator from 'tio-ui/components/paginator';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import humanize from 'tio-common/helpers/humanize';
import { resolveAuditUserName } from 'tio-common/utils/resolve-audit-user';
import Table from 'tio-ui/components/table';
import PageHeader from 'tio-ui/components/page-header';
import type { Company as CompanyModel } from 'tio-data/data/resources/company.type';
import type { TasPaymentExtension as PaymentModel } from 'tio-data/data/resources/tas-payment.ext';
import type { TOC } from '@ember/component/template-only';
import type { IntlService } from 'ember-intl';
import type RouterService from '@ember/routing/router-service';
import type { Store as StoreService } from '@warp-drive/core';
import { PhArrowFatLineDown } from 'ember-phosphor-icons';
import Select from 'tio-ui/components/select';

const PAYMENT_STATES = [
  'PROCESSING',
  'CREATED',
  'ABANDONED',
  'CANCELED',
  'PAID',
  'EMPTY',
  'ERRORED',
];

interface AdminTuitionAssistancePaymentsSignature {
  Args: {
    model: {
      tasPayments: PaymentModel[];
      total: number;
    };
    controller: {
      state: string;
      page: number;
    };
  };
}

export default class AdminTuitionAssistancePayments extends Component<AdminTuitionAssistancePaymentsSignature> {
  @service declare router: RouterService;
  @service declare store: StoreService;
  @service declare intl: IntlService;

  @tracked selectedPayment?: PaymentModel;
  @tracked selectedCompany?: CompanyModel;

  @tracked createModalOpen: boolean = false;
  @tracked settleModalOpen: boolean = false;
  @tracked cancelModalOpen: boolean = false;
  @tracked isSettlingPayment: boolean = false;
  @tracked isCancelingPayment: boolean = false;

  get settleablePayments() {
    const { tasPayments } = this.args.model;
    return tasPayments.filter((payment) => payment.state === 'CREATED');
  }

  get paymentOptions() {
    return this.settleablePayments.map((payment) => {
      return {
        key: payment.id!,
        label: `${payment.id} - ${this.intl.formatDate(payment.paymentDate)}`,
        payment,
      };
    });
  }

  setStateFilter = (value: string | null) => {
    this.args.controller.state = value ?? '';
  };

  setSelectedPayment = (key: string | null) => {
    this.selectedPayment = this.settleablePayments.find((p) => p.id === key);
  };

  toggleSettleModal = () => {
    this.settleModalOpen = !this.settleModalOpen;
  };

  closeSettleModal = () => {
    delete this.selectedPayment;
    this.toggleSettleModal();
  };

  settlePayment = async () => {
    this.isSettlingPayment = true;
    const paymentId = this.selectedPayment?.id;
    if (!paymentId) return;
    await this.store.request(settleTasPaymentRequest(paymentId));
    this.isSettlingPayment = false;
    this.closeSettleModal();
    this.selectedPayment = undefined;
    this.router.refresh(this.router.currentRoute?.name);
  };

  toggleCancelModal = () => {
    this.cancelModalOpen = !this.cancelModalOpen;
  };

  closeCancelModal = () => {
    delete this.selectedPayment;
    this.toggleCancelModal();
  };

  cancelPayment = async () => {
    const paymentId = this.selectedPayment?.id;
    if (!paymentId) return;
    this.isCancelingPayment = true;
    await this.store.request(cancelTasPaymentRequest(paymentId));
    this.isCancelingPayment = false;
    this.closeCancelModal();
    this.selectedPayment = undefined;
    this.router.refresh(this.router.currentRoute?.name);
  };

  downloadPaymentFiles = async (paymentId: string) => {
    if (!paymentId) return;
    await downloadTasPaymentFiles(paymentId, { apiKey: ENV.apiKey });
  };

  <template>
    {{pageTitle (t "common.tuition_assistance.payments.default")}}
    <VStack>
      <PageHeader @title={{t "tuition_assistance.payments"}} @sticky={{false}}>
        <:breadcrumbs as |b|>
          <b.crumb
            @route="authenticated.admin.tuition-assistance"
            @label={{t "tuition_assistance.default"}}
          />
          <b.crumb @label={{t "tuition_assistance.payments"}} />
        </:breadcrumbs>
      </PageHeader>
      <Section>
        <:body>
          <VStack>
            <Select
              @label="Filter by status"
              @items={{PAYMENT_STATES}}
              @selected={{@controller.state}}
              @onChange={{this.setStateFilter}}
            />
            <HStack>
              <Button @intent="primary" {{on "click" this.toggleSettleModal}}>
                {{t "tuition_assistance.admin.payments.settle"}}
              </Button>
              <Button @intent="danger" @appearance="outlined" {{on "click" this.toggleCancelModal}}>
                {{t "tuition_assistance.admin.payments.cancel"}}
              </Button>
            </HStack>
          </VStack>
        </:body>
      </Section>
      <Section>
        <:body>
          <Table @isLoading={{false}}>
            <:thead as |options|>
              <options.tr>
                <options.th>{{t
                    "common.tuition_assistance.payments.attribute_label.id"
                  }}</options.th>
                <options.th>{{t
                    "common.tuition_assistance.payments.attribute_label.payment_date"
                  }}</options.th>
                <options.th>{{t
                    "common.tuition_assistance.payments.attribute_label.status"
                  }}</options.th>
                <options.th>{{t
                    "common.tuition_assistance.payments.attribute_label.type"
                  }}</options.th>
                <options.th>{{t
                    "common.tuition_assistance.payments.attribute_label.manner"
                  }}</options.th>
                <options.th>{{t
                    "common.tuition_assistance.payments.attribute_label.created_by"
                  }}</options.th>
                <options.th>{{t
                    "common.tuition_assistance.payments.attribute_label.download"
                  }}</options.th>
              </options.tr>
            </:thead>
            <:tbody as |options|>
              {{#each @model.tasPayments as |payment|}}
                <options.tr>
                  <options.td>{{payment.id}}</options.td>
                  <options.td>
                    {{#if payment.paymentDate}}
                      {{safeFormatDate
                        payment.paymentDate
                        month="long"
                        day="2-digit"
                        year="numeric"
                      }}
                    {{/if}}
                  </options.td>
                  <options.td>{{humanize payment.state}}</options.td>
                  <options.td>{{payment.paymentType}}</options.td>
                  <options.td><MannerLabel @manner={{payment.paymentManner}} /></options.td>
                  <options.td><CreatedByName @value={{payment.createdBy}} /></options.td>
                  <options.td>
                    {{! @glint-expect-error}}
                    <button type="button" {{on "click" (fn this.downloadPaymentFiles payment.id)}}>
                      <PhArrowFatLineDown
                        @size="24"
                        @weight="fill"
                        class="tio-payment-download-icon"
                      />
                    </button>
                  </options.td>
                </options.tr>
              {{/each}}
              {{#if (eq @model.tasPayments.length 0)}}
                <options.tr>
                  <options.td colspan="7">
                    {{t "common.no_data"}}
                  </options.td>
                </options.tr>
              {{/if}}
            </:tbody>
            <:tfoot as |options|>
              <options.tr>
                <td colspan="7">
                  <Paginator @totalItems={{@model.tasPayments.length}} @summary={{true}} />
                </td>
              </options.tr>
            </:tfoot>
          </Table>
        </:body>
      </Section>
    </VStack>
    <Modal @isOpen={{this.settleModalOpen}} @onClose={{this.closeSettleModal}}>
      <:header>{{t "tuition_assistance.admin.payments.settle"}}</:header>
      <:body>
        <Select
          @label="Payment Runs"
          @items={{this.paymentOptions}}
          @selected={{or this.selectedPayment.id ""}}
          @onChange={{this.setSelectedPayment}}
          @searchable={{true}}
        />
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.isSettlingPayment}}
          @onCancel={{this.closeSettleModal}}
          @onSubmit={{this.settlePayment}}
          @submitText="Settle Payment"
        />
      </:footer>
    </Modal>
    <Modal @isOpen={{this.cancelModalOpen}} @onClose={{this.closeCancelModal}}>
      <:header>{{t "tuition_assistance.admin.payments.cancel"}}</:header>
      <:body>
        <Select
          @label="Payment Runs"
          @items={{this.paymentOptions}}
          @selected={{or this.selectedPayment.id ""}}
          @onChange={{this.setSelectedPayment}}
          @searchable={{true}}
        />
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.isCancelingPayment}}
          @onCancel={{this.closeCancelModal}}
          @onSubmit={{this.cancelPayment}}
          @submitText="Cancel Payment"
        />
      </:footer>
    </Modal>
  </template>
}

interface CreatedByNameSignature {
  Args: {
    value: PaymentModel['createdBy'];
  };
}

/**
 * `createdBy` is a PaperTrail whodunnit — resolve to a name only when it's a
 * real user id, never issue GET /users/<non-id> (404 → unhandled rejection).
 */
class CreatedByName extends Component<CreatedByNameSignature> {
  @service declare store: StoreService;

  createdByData = trackedFunction(this, () => resolveAuditUserName(this.store, this.args.value));

  get creatorName() {
    return this.createdByData.value || '';
  }

  <template>{{this.creatorName}}</template>
}

interface MannerLabelSignature {
  Args: {
    manner: PaymentModel['paymentManner'];
  };
}

// Translate the known manner enums (mirrors tio-admin's payments table); humanize
// anything unexpected — so the column reads correctly in non-English locales.
const MannerLabel: TOC<MannerLabelSignature> = <template>
  {{#if (eq @manner "REIMBURSEMENT")}}
    {{t "common.tuition_assistance.payments.manner.reimbursement"}}
  {{else if (eq @manner "COURSE_CASH")}}
    {{t "common.tuition_assistance.payments.manner.course_cash"}}
  {{else}}
    {{humanize @manner}}
  {{/if}}
</template>;
